import { useRef, useState } from 'react';
import type { Video } from '@/types/video';

export default function VideoPlayer({ video }: { video: Video }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'playing' | 'error'>('idle');
  const [retryKey, setRetryKey] = useState(0);

  const retry = () => {
    setStatus('idle');
    setRetryKey((k) => k + 1);
    const el = videoRef.current;
    if (el) {
      el.load();
    }
  };

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-xl border border-background-200/70 bg-black">
      <video
        key={retryKey}
        ref={videoRef}
        controls
        playsInline
        preload="metadata"
        poster={video.cover}
        className="h-full w-full bg-black object-contain"
        onWaiting={() => setStatus('loading')}
        onPlaying={() => setStatus('playing')}
        onCanPlay={() => setStatus((s) => (s === 'error' ? s : 'idle'))}
        onError={() => setStatus('error')}
      >
        <source src={video.src} type="video/mp4" />
        <track kind="captions" />
        你的浏览器不支持视频播放。
      </video>

      {status === 'loading' && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40">
          <span className="flex flex-col items-center gap-2 text-white">
            <i className="ri-loader-4-line animate-spin text-3xl" />
            <span className="text-xs">视频加载中…</span>
          </span>
        </div>
      )}

      {status === 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/80 p-6 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-500/20 text-primary-300">
            <i className="ri-error-warning-line text-2xl" />
          </span>
          <p className="text-sm font-medium text-white">演示视频加载失败</p>
          <p className="max-w-xs text-xs leading-relaxed text-foreground-500">
            本站为前端演示站点，不提供真实视频播放接口。你可以点击下方按钮重试。
          </p>
          <button
            type="button"
            onClick={retry}
            className="mt-1 flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-400"
          >
            <i className="ri-refresh-line" />
            重新加载
          </button>
        </div>
      )}
    </div>
  );
}